/**
 * useNativeAppLifecycle — React glue that wires the platform layer to
 * the running app.
 *
 * The hook is intentionally boring: it registers cleanup-scoped
 * listeners for the events the customer app actually cares about and
 * dispatches to focused callbacks. Every native listener is registered
 * exactly once (per mount) and torn down on cleanup — the audit called
 * out listener leaks on rerender as an anti-pattern and this hook
 * exists to keep the fix in one place.
 *
 * Responsibilities:
 *   - App resume → call `onResume` (charging session rehydration lives
 *     here in App.jsx via SessionProvider glue).
 *   - Connectivity change → call `onNetworkChange` with the new
 *     `{ connected, connectionType }` snapshot.
 *   - Deep-link opened (cold + warm) → call `onDeepLink` with the
 *     resolved React Router path.
 *   - Android hardware back button → call `onBack`. The consumer
 *     returns `true` to signal the press was consumed; the default
 *     handler in lifecycle.js closes the WebView otherwise.
 *
 * The hook doesn't own any state. Callers manage their own — see
 * `NativeBridge` in AppShell for the reference wiring.
 */
import { useEffect } from 'react';
import { getLaunchUrl, onAppUrlOpen, onBackButton, onResume } from './lifecycle.js';
import { getStatus, onStatusChange } from './network.js';
import { resolveDeepLink } from './deepLinks.js';

export function useNativeAppLifecycle({
  onResume: onResumeCb,
  onNetworkChange,
  onDeepLink,
  onBack,
} = {}) {
  // App resume ─────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof onResumeCb !== 'function') return undefined;
    return onResume(() => { try { onResumeCb(); } catch { /* consumer bug — never crash bootstrap */ } });
  }, [onResumeCb]);

  // Network state — fire once with the current status, then subscribe
  // for future changes. The initial fire lets the offline banner match
  // reality on mount without waiting for the first transition.
  useEffect(() => {
    if (typeof onNetworkChange !== 'function') return undefined;
    let cancelled = false;
    getStatus().then((status) => { if (!cancelled) onNetworkChange(status); }).catch(() => {});
    const unsubscribe = onStatusChange((status) => {
      if (!cancelled) onNetworkChange(status);
    });
    return () => { cancelled = true; unsubscribe(); };
  }, [onNetworkChange]);

  // Deep links — cold start via getLaunchUrl + warm via appUrlOpen.
  useEffect(() => {
    if (typeof onDeepLink !== 'function') return undefined;
    let cancelled = false;
    // Cold-start URL: only fire once, on mount.
    getLaunchUrl().then((url) => {
      if (cancelled || !url) return;
      const resolved = resolveDeepLink(url);
      if (resolved) onDeepLink(resolved, { source: 'cold' });
    }).catch(() => {});
    const unsubscribe = onAppUrlOpen((event) => {
      if (cancelled) return;
      const resolved = resolveDeepLink(event?.url || '');
      if (resolved) onDeepLink(resolved, { source: 'warm' });
    });
    return () => { cancelled = true; unsubscribe(); };
  }, [onDeepLink]);

  // Android back button ────────────────────────────────────────────
  useEffect(() => {
    if (typeof onBack !== 'function') return undefined;
    return onBackButton((info) => {
      try { return onBack(info) === true; } catch { return false; }
    });
  }, [onBack]);
}
